import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Trash2, Plus, Save, X, Loader2, Stethoscope } from "lucide-react";
import { customerSystemsApi, vendorsApi } from "@/lib/api";
import { useToast } from "@/components/SimpleToast";
import { useCustomerSystems, reloadCustomerSystems } from "@/hooks/useCustomerSystems";
import { SYSTEM_COLORS } from "./customerConstants";

const EMPTY = { name: "", short_label: "", color: "teal", vendor_id: null };
const errMsg = (e, f) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : f);

function SystemForm({ form, setForm, onSave, onCancel, saving, testId, vendors }) {
  return (
    <div className="rounded-lg border border-primary/40 p-3 space-y-3" data-testid={testId}>
      <div className="grid gap-2 sm:grid-cols-2">
        <Input placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="system-form-name" />
        <Input placeholder="Kurzname (optional)" value={form.short_label} onChange={(e) => setForm({ ...form, short_label: e.target.value })} data-testid="system-form-short" />
      </div>
      <Select value={form.vendor_id || "none"} onValueChange={(v) => setForm({ ...form, vendor_id: v === "none" ? null : v })}>
        <SelectTrigger data-testid="system-form-vendor"><SelectValue placeholder="Hersteller (Arztsoftware)" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Kein Hersteller verknüpft</SelectItem>
          {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="flex flex-wrap gap-1.5" data-testid="system-form-colors">
        {Object.entries(SYSTEM_COLORS).map(([key, cls]) => (
          <button key={key} type="button" onClick={() => setForm({ ...form, color: key })} title={key}
            className={`w-7 h-7 rounded-full ${cls} ${form.color === key ? "ring-2 ring-offset-2 ring-current" : ""}`} data-testid={`system-color-${key}`} />
        ))}
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="outline" onClick={onCancel}><X className="w-4 h-4 mr-1" />Abbrechen</Button>
        <Button type="button" size="sm" onClick={onSave} disabled={saving} data-testid="system-form-save">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Speichern
        </Button>
      </div>
    </div>
  );
}

export function CustomerSystemsManager({ open, onOpenChange, onRenamed, onDeleted }) {
  const toast = useToast();
  const systems = useCustomerSystems();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [vendors, setVendors] = useState([]);
  useEffect(() => { vendorsApi.list().then((r) => setVendors(r.data)).catch(() => {}); }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Bitte einen Namen eingeben");
    setSaving(true);
    try {
      if (editing === "new") {
        await customerSystemsApi.create(form);
        toast.success("System angelegt");
      } else {
        const old = systems.find((s) => s.id === editing);
        const res = await customerSystemsApi.update(editing, form);
        if (old && old.name !== res.data.name) onRenamed?.(old.name, res.data.name);
        toast.success(res.data.renamed_customers ? `System gespeichert – bei ${res.data.renamed_customers} Kunden umbenannt` : "System gespeichert");
      }
      await reloadCustomerSystems();
      setEditing(null);
    } catch (e) {
      toast.error(errMsg(e, "Speichern fehlgeschlagen"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    const hint = s.usage_count ? ` Es wird bei ${s.usage_count} Kunden entfernt.` : "";
    if (!window.confirm(`System „${s.name}“ wirklich löschen?${hint}`)) return;
    try {
      const res = await customerSystemsApi.delete(s.id);
      onDeleted?.(s.name);
      await reloadCustomerSystems();
      toast.success(res.data.removed_from_customers ? `System gelöscht – bei ${res.data.removed_from_customers} Kunden entfernt` : "System gelöscht");
    } catch (e) {
      toast.error(errMsg(e, "Löschen fehlgeschlagen"));
    }
  };

  const formProps = { form, setForm, onSave: save, onCancel: () => setEditing(null), saving, vendors };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="systems-manager-dialog">
        <DialogHeader>
          <DialogTitle>Systeme verwalten</DialogTitle>
          <DialogDescription>Änderungen gelten sofort für alle Kunden.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {systems.map((s) =>
            editing === s.id ? (
              <SystemForm key={s.id} {...formProps} testId={`system-edit-form-${s.id}`} />
            ) : (
              <div key={s.id} className="flex items-center gap-3 rounded-lg border px-3 py-2" data-testid={`system-row-${s.id}`}>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${SYSTEM_COLORS[s.color] || SYSTEM_COLORS.slate}`}>{s.short_label || s.name}</span>
                <span className="flex-1 min-w-0 text-sm text-muted-foreground truncate">
                  {s.name !== s.short_label ? s.name : ""}
                  {s.vendor_name && <span className="inline-flex items-center gap-1 ml-1 text-xs text-primary" data-testid={`system-vendor-${s.id}`}><Stethoscope className="w-3 h-3" />{s.vendor_name}</span>}
                </span>
                <span className="text-xs text-muted-foreground shrink-0">{s.usage_count} Kunden</span>
                <Button type="button" variant="ghost" size="icon" onClick={() => { setEditing(s.id); setForm({ name: s.name, short_label: s.short_label || "", color: s.color, vendor_id: s.vendor_id || null }); }} title="Bearbeiten" data-testid={`system-edit-${s.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button type="button" variant="ghost" size="icon" onClick={() => remove(s)} title="Löschen" data-testid={`system-delete-${s.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            )
          )}
          {editing === "new" ? (
            <SystemForm {...formProps} testId="system-new-form" />
          ) : (
            <Button type="button" variant="outline" className="w-full" onClick={() => { setEditing("new"); setForm(EMPTY); }} data-testid="system-add-btn"><Plus className="w-4 h-4 mr-1.5" />System hinzufügen</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
